import { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import { apiFetch } from '../../services/api';

const TYPE_LABELS = { school:'School', university:'University', company:'Company', ngo:'NGO', government:'Government' };
const TYPE_COLORS = { school:'var(--blue-light)', university:'var(--purple-light)', company:'var(--accent)', ngo:'var(--green-light)', government:'var(--yellow)' };

export default function PartnersPage() {
  const [tab, setTab]             = useState('institutions');
  const [partners, setPartners]   = useState([]);
  const [students, setStudents]   = useState([]);
  const [programs, setPrograms]   = useState([]);
  const [cohorts, setCohorts]     = useState([]);
  const [internships, setInterns] = useState([]);
  const [users, setUsers]         = useState([]);
  const [pagination, setPag]      = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [selected, setSel]        = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({});
  const [batchForm, setBatch]     = useState({});
  const [filterInst, setFilterInst] = useState('');
  const [linked, setLinked]       = useState(null);
  const [waPhone, setWaPhone]     = useState('');

  const token   = localStorage.getItem('mh_token');
  const headers = { 'Content-Type':'application/json', Authorization:`Bearer ${token}` };

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await apiFetch('/admin/partners?limit=50', { headers });
      const data = await res.json();
      setPartners(data.data || []); setPag(data.pagination);
    } catch { setPartners([]); } finally { setLoading(false); }
  }, []);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit:50 });
      if (filterInst) params.set('institution_id', filterInst);
      const res  = await apiFetch(`/admin/partner-students?${params}`, { headers });
      const data = await res.json();
      setStudents(data.data || []); setPag(data.pagination);
    } catch { setStudents([]); } finally { setLoading(false); }
  }, [filterInst]);

  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { if (tab === 'students') loadStudents(); }, [tab, loadStudents]);

  const loadSelectData = async () => {
    try {
      const [pRes, cRes, iRes, uRes] = await Promise.all([
        apiFetch('/programs?limit=100', { headers }),
        apiFetch('/programs/cohorts',   { headers }),
        apiFetch('/internships?limit=100', { headers }),
        apiFetch('/users?limit=200',    { headers }),
      ]);
      const [pData, cData, iData, uData] = await Promise.all([pRes.json(), cRes.json(), iRes.json(), uRes.json()]);
      // programs/cohorts returns { data: rows[] } — rows array directly
      setPrograms(pData.data  || []);
      setCohorts(Array.isArray(cData.data) ? cData.data : []);
      setInterns(iData.data   || []);
      setUsers(uData.data     || []);
    } catch (e) { console.error('loadSelectData failed', e); }
  };

  const emptyForm = () => ({
    name:'', slug:'', type:'school',
    rep_name:'', rep_title:'', rep_email:'', rep_phone:'',
    contact_name:'', contact_email:'', contact_phone:'',
    address:'', website:'', description:'',
    partnership_type:'', notes:'', user_id:'',
  });

  const openCreate = async () => { await loadSelectData(); setForm(emptyForm()); setSel(null); setModal('partner'); };
  const openEdit   = async (p) => {
    await loadSelectData();
    setForm({
      name:p.name, slug:p.slug, type:p.type,
      rep_name:p.rep_name||'', rep_title:p.rep_title||'',
      rep_email:p.rep_email||'', rep_phone:p.rep_phone||'',
      contact_name:p.contact_name||'', contact_email:p.contact_email||'',
      contact_phone:p.contact_phone||'', address:p.address||'',
      website:p.website||'', description:p.description||'',
      partnership_type:p.partnership_type||'', status:p.status,
      notes:p.notes||'', user_id:p.user_id||'',
    });
    setSel(p); setModal('partner');
  };

  const openBatch = async () => {
    await loadSelectData();
    setBatch({ institution_id:'', program_id:'', cohort_id:'', internship_id:'', students:'' });
    setModal('batch');
  };

  const handleSavePartner = async () => {
    setSaving(true);
    try {
      const prevUserId = selected?.user_id;
      const newUserId  = form.user_id || null;
      if (!selected) {
        await apiFetch('/admin/partners', { method:'POST', headers, body:JSON.stringify(form) });
      } else {
        await apiFetch(`/admin/partners/${selected.id}`, { method:'PUT', headers, body:JSON.stringify(form) });
      }
      if (newUserId && newUserId !== prevUserId) {
        const linkedUser = users.find(u => u.id === newUserId);
        if (linkedUser) {
          setLinked({ name:linkedUser.name, email:linkedUser.email, institution:form.name||selected?.name, rep_name:form.rep_name });
          setWaPhone(linkedUser.phone || '');
        }
      }
      setModal(null); loadPartners();
    } catch { alert('Save failed'); } finally { setSaving(false); }
  };

  const handleBatchEnroll = async () => {
    setSaving(true);
    try {
      const parsed = batchForm.students.split('\n')
        .map(l => l.trim()).filter(Boolean)
        .map(line => {
          const [name, email, phone, student_id_ref] = line.split(',').map(s => s.trim());
          return { name, email, phone:phone||'', student_id_ref:student_id_ref||'' };
        }).filter(s => s.name && s.email);

      const payload = {
        institution_id: batchForm.institution_id,
        program_id:     batchForm.program_id   || null,
        cohort_id:      batchForm.cohort_id    || null,
        internship_id:  batchForm.internship_id|| null,
        students:       parsed,
      };
      const res  = await apiFetch('/admin/partners/batch-enroll', { method:'POST', headers, body:JSON.stringify(payload) });
      const data = await res.json();
      alert(`✓ Enrolled ${data.data?.enrolled || 0} students successfully`);
      setModal(null); loadStudents();
    } catch { alert('Batch enrollment failed'); } finally { setSaving(false); }
  };

  const spaUrl = window.location.origin.replace('3001', '3000');

  const sendLinkedWhatsApp = () => {
    if (!linked) return;
    const phone = waPhone.replace(/\D/g, '');
    const text  = encodeURIComponent(`*MilleHills Partner Portal Access*\n\nHi ${linked.name}!\n\nYour partner portal account for *${linked.institution}* has been activated.\n\n🔗 Login: ${spaUrl}/login\n📧 Email: ${linked.email}\n\nYou can now view all your institution's students and track their progress.\n\n— MilleHills Team`);
    window.open(phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`, '_blank');
  };

  // Cohorts filtered by selected program in batch form
  const filteredCohorts = cohorts.filter(c => !batchForm.program_id || c.program_id === batchForm.program_id);

  const PARTNER_COLS = [
    { key:'name', label:'Institution', render:(v,row) => (
      <div>
        <div style={{ fontWeight:600 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
          {row.type && <span style={{ color:TYPE_COLORS[row.type], fontWeight:600, marginRight:6 }}>{TYPE_LABELS[row.type]}</span>}
          {row.partnership_type && <span>{row.partnership_type}</span>}
        </div>
      </div>
    )},
    { key:'rep_name', label:'Representative', render:(v,row) => v ? (
      <div>
        <div style={{ fontWeight:500, fontSize:13 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.rep_title||''}{row.rep_title&&row.rep_phone?' · ':''}{row.rep_phone||''}</div>
      </div>
    ) : (
      <div>
        <div style={{ fontSize:13 }}>{row.contact_name||'—'}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.contact_email||''}</div>
      </div>
    )},
    { key:'student_count', label:'Students', render:v => <span style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:15, color:'var(--accent)' }}>{v||0}</span> },
    { key:'portal_email', label:'Portal Access', render:v => v
      ? <span style={{ fontSize:11, color:'var(--green-light)' }}>✓ {v}</span>
      : <span style={{ fontSize:11, color:'var(--text-muted)' }}>Not linked</span>
    },
    { key:'status', label:'Status', render:v => <Badge status={v}/> },
    { key:'_actions', label:'', render:(_,row) => (
      <div style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={()=>openEdit(row)}>Edit</Button>
      </div>
    )},
  ];

  const STUDENT_COLS = [
    { key:'student_name', label:'Student', render:(v,row) => (
      <div><div style={{ fontWeight:500 }}>{v}</div><div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.student_email}{row.student_id_ref?` · ${row.student_id_ref}`:''}</div></div>
    )},
    { key:'institution_name', label:'Institution',  render:v => <span style={{ fontSize:12 }}>{v}</span> },
    { key:'program_title',    label:'Program',      render:(v,row) => <span style={{ fontSize:12 }}>{v||row.internship_title||'—'}</span> },
    { key:'cohort_name',      label:'Cohort',       render:v => <span style={{ fontSize:11, color:'var(--text-muted)' }}>{v||'—'}</span> },
    { key:'enrollment_status',label:'Status',       render:v => v?<Badge status={v}/>:'—' },
    { key:'progress_pct',     label:'Progress',     render:v => (
      <div style={{ minWidth:80 }}>
        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:2 }}>{v||0}%</div>
        <div style={{ height:3, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${v||0}%`, background:'var(--accent)', borderRadius:2 }}/>
        </div>
      </div>
    )},
    { key:'certificate_hash', label:'Cert', render:v => v?<span style={{ fontSize:10, color:'var(--green-light)' }}>✓ Issued</span>:'—' },
  ];

  const tabBtn = (t) => ({ padding:'9px 18px', fontSize:13, cursor:'pointer', background:'none', border:'none', borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`, color:tab===t?'var(--accent)':'var(--text-muted)', fontWeight:tab===t?600:400, marginBottom:-1, transition:'all 0.15s' });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
      <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)' }}>
        <button style={tabBtn('institutions')} onClick={()=>setTab('institutions')}>Partner Institutions</button>
        <button style={tabBtn('students')}     onClick={()=>setTab('students')}>Partner Students</button>
      </div>

      {tab === 'institutions' && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>{pagination?.total??0} partners</span>
            <div style={{ display:'flex', gap:8 }}>
              <Button variant="secondary" onClick={openBatch}>⬡ Batch Enroll</Button>
              <Button onClick={openCreate}>+ Add Partner</Button>
            </div>
          </div>
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <Table columns={PARTNER_COLS} data={partners} loading={loading} onRowClick={openEdit}/>
            <Pagination pagination={pagination} onPage={()=>{}}/>
          </div>
        </>
      )}

      {tab === 'students' && (
        <>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <Select value={filterInst} onChange={e=>setFilterInst(e.target.value)} style={{ width:240 }}>
              <option value="">All Institutions</option>
              {partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>{pagination?.total??0} students</span>
          </div>
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <Table columns={STUDENT_COLS} data={students} loading={loading}/>
            <Pagination pagination={pagination} onPage={()=>{}}/>
          </div>
        </>
      )}

      {/* Partner modal */}
      <Modal open={modal==='partner'} onClose={()=>setModal(null)} title={selected?`Edit — ${selected.name}`:'New Partner Institution'} width={700}>
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          <div>
            <div style={sectionLabel}>Institution Details</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Input label="Institution Name" value={form.name||''} onChange={e=>{
                  const slug = e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
                  setForm(f=>({...f,name:e.target.value,slug}));
                }}/>
              </div>
              <Select label="Type" value={form.type||'school'} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                {Object.entries(TYPE_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </Select>
              <Input label="Partnership Type" value={form.partnership_type||''} onChange={e=>setForm(f=>({...f,partnership_type:e.target.value}))} placeholder="MoU, Commercial, Community"/>
              <Input label="Website" value={form.website||''} onChange={e=>setForm(f=>({...f,website:e.target.value}))}/>
              <Input label="Address" value={form.address||''} onChange={e=>setForm(f=>({...f,address:e.target.value}))}/>
              {selected && (
                <Select label="Status" value={form.status||'active'} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              )}
            </div>
          </div>

          <div style={{ padding:16, background:'rgba(78,156,245,0.06)', border:'1px solid rgba(78,156,245,0.2)', borderRadius:10 }}>
            <div style={sectionLabel}>Institution Representative</div>
            <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12, lineHeight:1.5 }}>
              The person who represents the institution — signs MOUs, receives official correspondence.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <Input label="Full Name"       value={form.rep_name||''}  onChange={e=>setForm(f=>({...f,rep_name:e.target.value}))}  placeholder="Dr. Jean Uwimana"/>
              <Input label="Title / Position" value={form.rep_title||''} onChange={e=>setForm(f=>({...f,rep_title:e.target.value}))} placeholder="Principal, HR Director…"/>
              <Input label="Email"           value={form.rep_email||''} onChange={e=>setForm(f=>({...f,rep_email:e.target.value}))} placeholder="rep@institution.rw"/>
              <Input label="Phone / WhatsApp" value={form.rep_phone||''} onChange={e=>setForm(f=>({...f,rep_phone:e.target.value}))} placeholder="+250 7xx xxx xxx"/>
            </div>
          </div>

          <div style={{ padding:16, background:'rgba(62,207,142,0.06)', border:'1px solid rgba(62,207,142,0.2)', borderRadius:10 }}>
            <div style={sectionLabel}>Portal Login Account</div>
            <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12, lineHeight:1.5 }}>
              The user who logs into MilleHills to track students. Create their account in <strong>Users & Roles</strong> first.
            </p>
            <Select value={form.user_id||''} onChange={e=>setForm(f=>({...f,user_id:e.target.value}))}>
              <option value="">— No portal access yet —</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name} ({u.email}) — {u.role}</option>)}
            </Select>
            {form.user_id && <div style={{ marginTop:8, fontSize:11, color:'#3ecf8e' }}>✓ This user will be upgraded to <strong>partner</strong> role</div>}
          </div>

          <div>
            <label style={labelStyle}>Description / Notes</label>
            <textarea value={form.description||''} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={3}
              style={taStyle}/>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
            <Button onClick={handleSavePartner} loading={saving}>{selected?'Save Changes':'Create Partner'}</Button>
          </div>
        </div>
      </Modal>

      {/* Batch enroll modal */}
      <Modal open={modal==='batch'} onClose={()=>setModal(null)} title="Batch Enroll Students" width={640}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ padding:'12px 16px', background:'rgba(232,164,58,0.08)', border:'1px solid rgba(232,164,58,0.25)', borderRadius:8, fontSize:12, color:'var(--text-secondary)', lineHeight:1.6 }}>
            One student per line: <code style={{ color:'var(--accent)' }}>Full Name, Email, Phone, StudentID</code><br/>
            Phone and StudentID are optional. Name and Email are required.
          </div>

          <Select label="Partner Institution" value={batchForm.institution_id||''} onChange={e=>setBatch(f=>({...f,institution_id:e.target.value}))}>
            <option value="">— Select institution —</option>
            {partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Select label="Program (optional)" value={batchForm.program_id||''} onChange={e=>setBatch(f=>({...f,program_id:e.target.value,cohort_id:''}))}>
              <option value="">— No program —</option>
              {programs.map(p=><option key={p.id} value={p.id}>{p.title||p.name}</option>)}
            </Select>
            <Select label="Cohort (optional)" value={batchForm.cohort_id||''} onChange={e=>setBatch(f=>({...f,cohort_id:e.target.value}))}>
              <option value="">— No cohort —</option>
              {filteredCohorts.map(c=>(
                <option key={c.id} value={c.id}>
                  {c.cohort_name} — {c.program_title||''} {c.start_date?`(${new Date(c.start_date).toLocaleDateString('en-RW',{month:'short',year:'numeric'})})` :''}
                </option>
              ))}
            </Select>
            <div style={{ gridColumn:'1/-1' }}>
              <Select label="Internship (optional)" value={batchForm.internship_id||''} onChange={e=>setBatch(f=>({...f,internship_id:e.target.value}))}>
                <option value="">— No internship —</option>
                {internships.map(i=><option key={i.id} value={i.id}>{i.title}</option>)}
              </Select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Students (CSV format)</label>
            <textarea value={batchForm.students||''} onChange={e=>setBatch(f=>({...f,students:e.target.value}))} rows={8}
              placeholder={"Alice Uwimana, alice@email.com, +250780000001, STU001\nBob Nkusi, bob@email.com\nClaire Mukeza, claire@email.com, +250780000003"}
              style={{ ...taStyle, lineHeight:1.8 }}/>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
              {(batchForm.students||'').split('\n').filter(l=>l.trim()).length} students ready
            </div>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
            <Button onClick={handleBatchEnroll} loading={saving}
              disabled={!batchForm.institution_id||!(batchForm.students||'').trim()}>
              Enroll Students
            </Button>
          </div>
        </div>
      </Modal>

      {/* Portal linked confirmation */}
      <Modal open={!!linked} onClose={()=>setLinked(null)} title="Portal Access Activated" width={480}>
        {linked && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ padding:'14px 16px', background:'rgba(62,207,142,0.08)', border:'1px solid rgba(62,207,142,0.3)', borderRadius:10, fontSize:13, lineHeight:2, color:'var(--text-secondary)' }}>
              <div>✓ <strong>{linked.name}</strong> can now log into the partner portal</div>
              <div>✓ Role upgraded to <strong>partner</strong></div>
              <div>✓ Linked to <strong>{linked.institution}</strong></div>
              {linked.rep_name && <div>✓ Rep: <strong>{linked.rep_name}</strong></div>}
            </div>
            <div>
              <label style={{ ...labelStyle }}>Notify via WhatsApp</label>
              <div style={{ display:'flex', gap:8 }}>
                <input value={waPhone} onChange={e=>setWaPhone(e.target.value)} placeholder="+250 7xx xxx xxx"
                  style={{ flex:1, background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13 }}/>
                <button onClick={sendLinkedWhatsApp}
                  style={{ padding:'9px 18px', borderRadius:'var(--radius-sm)', background:'#25D366', color:'#fff', border:'none', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                  Send →
                </button>
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Button onClick={()=>{navigator.clipboard.writeText(`Partner Portal: ${spaUrl}/login\nEmail: ${linked.email}`);alert('Copied!');}} variant="secondary" style={{ flex:1, justifyContent:'center' }}>Copy Link</Button>
              <Button onClick={()=>setLinked(null)} style={{ flex:1, justifyContent:'center' }}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const sectionLabel = { fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:12 };
const labelStyle   = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };
const taStyle      = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'monospace', boxSizing:'border-box' };
